import type { Persona, Subscale } from '../types/survey';
import type { MetaTypeId } from '../types/richPersona';

// ─────────────────────────────────────────────────
// Coaching Messages — situation × persona
// All user-facing "coach speech" copy lives here.
// ─────────────────────────────────────────────────

type PersonaMessages = Record<Persona, string>;
type PersonaMessagesArray = Record<Persona, string[]>;

// ─── Onboarding ──────────────────────────────────

export const ONBOARDING_STEP1_SPEECH: PersonaMessages = {
  elementary: '안녕! 만나서 반가워 😊\n나는 네가 공부하고 성장하는 걸 도와주는 친구야.\n먼저 너에 대해 좀 알고 싶어 — 어디에 가까워?',
  secondary: '안녕! 만나서 반가워.\n너한테 딱 맞는 분석을 해주려면 먼저 알아야 할 게 있어.\n가장 가까운 그룹을 골라줘!',
  university: '반갑습니다! 맞춤 분석을 위해\n먼저 어떤 그룹에 가까운지 알려주세요.',
  professional: '반갑습니다. 맞춤 분석을 제공해드리기 위해\n먼저 가장 가까운 그룹을 선택해주세요.',
};

export const ONBOARDING_STEP1_CONFIRM: PersonaMessages = {
  elementary: '좋아! 반가워! 👋',
  secondary: '좋아, 반가워! 👋',
  university: '감사합니다! 반갑습니다.',
  professional: '감사합니다. 반갑습니다.',
};

export const ONBOARDING_STEP2_SPEECH: PersonaMessages = {
  elementary: '좋아! 이제 너에 대해 조금만 더 물어볼게.\n어렵지 않아, 편하게 골라줘!',
  secondary: '고마워! 몇 가지만 더 알려주면\n너한테 딱 맞는 분석을 해줄 수 있어.',
  university: '감사합니다. 몇 가지 추가 정보를 알려주시면\n더 정확한 맞춤 분석이 가능해요.',
  professional: '감사합니다. 몇 가지 추가 정보를 입력해주시면\n보다 정밀한 분석을 제공해드리겠습니다.',
};

export const ONBOARDING_STEP3_SPEECH: PersonaMessages = {
  elementary: '잠깐만! 네가 알려준 것들을 분석하고 있어... 🔍',
  secondary: '네가 알려준 정보를 바탕으로 분석 중이야.\n잠깐만 기다려줘!',
  university: '입력하신 정보를 분석하고 있어요.\n잠시만 기다려주세요 ✨',
  professional: '입력하신 정보를 바탕으로 프로필을 분석하고 있습니다.',
};

export const ONBOARDING_TRANSITION_SPEECH: PersonaMessages = {
  elementary: '좋아, 준비됐어! 🎉\n이제 재미있는 걸 해볼 건데 — 10개 질문에 답하면\n네가 어떤 유형인지 알 수 있어. 정답은 없어!',
  secondary: '프로필 완성! 이제 진짜 시작이야. ✨\n10개 질문으로 너의 유형을 알아볼 거야.\n같은 유형 친구들이 어떤 전략을 쓰는지도 알려줄게.',
  university: '프로필이 준비되었어요!\n이제 10문항으로 유형을 분석합니다.\n유형을 알면 맞춤 전략과 루틴을 추천해드릴 수 있어요.',
  professional: '프로필 설정이 완료되었습니다.\n다음은 10문항의 유형 진단입니다.\n결과를 바탕으로 맞춤 성장 전략을 제안해드립니다.',
};

export const ONBOARDING_TRANSITION_CTA: PersonaMessages = {
  elementary: '가보자!',
  secondary: '시작하기',
  university: '시작하기',
  professional: '시작하겠습니다',
};

// ─── Discovery ───────────────────────────────────

export const DISCOVERY_INTRO_FIRST: PersonaMessages = {
  elementary: '자, 이제 너만의 유형을 찾아볼 시간이야! 🧭\n세상에 나쁜 유형은 없어 — 어떤 유형이든 다 멋진 능력이 있거든.\n느끼는 대로 편하게 골라줘!',
  secondary: '이제 네 유형을 알아볼 거야.\n같은 문제를 보고도 사람마다 접근법이 다른데,\n너는 어떤 스타일인지 알면 공부가 훨씬 수월해져.',
  university: '지금부터 유형을 진단합니다.\n왜 어떤 방법은 잘 맞고 어떤 건 안 맞는지,\n유형을 알면 이해할 수 있어요. 10문항, 2분이면 충분합니다.',
  professional: '유형 진단을 시작합니다.\n같은 목표도 사람마다 최적의 방식이 다릅니다.\n당신의 스타일을 파악하면, 에너지를 덜 쓰고 더 좋은 결과를 낼 수 있습니다.',
};

export const DISCOVERY_INTRO_CTA: PersonaMessages = {
  elementary: '시작하기!',
  secondary: '시작하기',
  university: '진단 시작',
  professional: '진단 시작',
};

export function getDiscoveryRevisitSpeech(
  persona: Persona,
  emoji: string,
  typeName: string,
  nameKo: string,
): string {
  const msgs: PersonaMessages = {
    elementary: `앗, ${emoji} ${typeName}!\n너 유형 이미 찾았잖아! 기억나지?\n지금 컨디션이 궁금하면 바로 체크하러 가자!`,
    secondary: `${emoji} ${typeName} — 이미 유형을 알고 있지?\n지금 상태를 다시 체크하면, 지난번이랑 비교할 수 있어.`,
    university: `이전에 ${typeName}(${nameKo}) 유형으로 진단받으셨어요.\n상태 측정을 하면 이전 결과와 비교해서 성장 추이를 확인할 수 있습니다.`,
    professional: `${typeName}(${nameKo}) 유형 진단 완료 상태입니다.\n상태 측정을 하시면 이전 결과와 비교 분석을 제공합니다.`,
  };
  return msgs[persona];
}

export const DISCOVERY_RESULT_ANNOUNCE: PersonaMessages = {
  elementary: '두구두구... 🥁 네 유형이 나왔어!',
  secondary: '분석 완료! 네 유형을 알아냈어.',
  university: '분석이 완료되었어요. 당신의 유형을 소개합니다.',
  professional: '분석이 완료되었습니다. 결과를 안내드립니다.',
};

// ─── Discovery Result → Survey Bridge ────────────

export function getDiscoveryBridgeSpeech(
  persona: Persona,
  metaTypeId: MetaTypeId,
): string {
  const msgs: Record<MetaTypeId, PersonaMessages> = {
    campfire: {
      elementary: '🔥 모닥불 유형은 에너지가 넘쳐!\n에너지가 높을 때와 낮을 때 방법을 다르게 하면 훨씬 잘돼.\n지금 네 에너지가 어디쯤인지 체크해볼까? 딱 5분이면 돼!',
      secondary: '🔥 모닥불 유형의 에너지는 큰 강점이야.\n에너지 패턴에 맞춰 공부하면 효율이 확 올라가거든.\n지금 상태를 체크해보면 맞춤 전략을 알려줄 수 있어.',
      university: '🔥 모닥불 유형의 실행력이 돋보여요.\n현재 상태를 측정하면, 에너지를 가장 효과적으로 쓸 수 있는 전략을 만들어드릴 수 있어요.',
      professional: '🔥 모닥불 유형의 추진력이 인상적입니다.\n현재 상태를 측정하면 에너지를 극대화할 맞춤 전략을 제안해드립니다.',
    },
    compass: {
      elementary: '🧭 나침반 유형은 깊이 생각하는 게 강점이야!\n여기에 \'계획\' 습관 하나만 추가하면 훨씬 잘할 수 있어.\n지금 어디가 강하고 어디를 키우면 좋을지 확인해볼래?',
      secondary: '🧭 나침반 유형은 깊이 생각하는 게 강점이야.\n이 유형이 \'계획\' 습관 하나만 추가하면 성적이 확 올라간 경우가 많아.\n지금 어디가 강하고 어디를 키우면 좋을지 확인해볼래?',
      university: '🧭 나침반 유형의 성찰력은 큰 강점이에요.\n현재 상태를 측정하면, 성찰력을 실천으로 연결할 구체적인 전략을 만들어드릴 수 있어요.',
      professional: '🧭 나침반 유형의 통찰력이 돋보입니다.\n현재 상태를 측정하면 성찰을 실천으로 연결하는 전략을 제안해드립니다.',
    },
    blueprint: {
      elementary: '📐 설계가 유형은 계획 잘 세우는 게 강점이야!\n근데 가끔 계획만 세우고 시작을 못 할 때가 있거든.\n지금 어디가 강하고 어디를 키우면 좋을지 체크해볼까?',
      secondary: '📐 설계가 유형의 분석력은 진짜 강점이야.\n어디가 강하고 어디를 더 키우면 좋을지 확인해볼래?\n많은 설계가 유형 친구들이 이 단계에서 숨은 강점을 발견했어.',
      university: '📐 설계가 유형의 분석력은 큰 강점이에요.\n현재 상태를 측정하면, 유형에 맞는 구체적인 성장 전략을 만들어드릴 수 있어요.\n많은 설계가 유형 사용자들이 이 단계에서 숨은 강점을 발견했어요.',
      professional: '📐 설계가 유형의 체계적 사고가 돋보입니다.\n현재 상태를 측정하면 분석력을 극대화할 지점을 정확히 짚어드릴 수 있습니다.',
    },
    stopwatch: {
      elementary: '⏱️ 실행가 유형은 바로바로 행동하는 게 강점이야!\n근데 가끔 준비 없이 달리기부터 할 때가 있거든.\n지금 어디가 강하고 어디를 키우면 좋을지 체크해볼까?',
      secondary: '⏱️ 실행가 유형의 실행력이 대단해.\n근데 실행력을 더 잘 쓰려면 어디에 집중하면 좋을지 알면 좋겠지?\n상태 확인을 하면 딱 그걸 알 수 있어.',
      university: '⏱️ 실행가 유형의 실행력이 돋보여요.\n현재 상태를 측정하면, 실행력을 극대화할 지점을 정확히 짚어드릴 수 있어요.\n이 진단이 앞으로의 성장 기준점이 됩니다.',
      professional: '⏱️ 실행가 유형의 실행력이 돋보입니다.\n현재 상태를 측정하면, 실행력을 극대화할 지점을 정확히 짚어드릴 수 있습니다.\n이 진단이 앞으로의 성장 기준점이 됩니다.',
    },
  };
  return msgs[metaTypeId][persona];
}

// ─── Discovery Result 3 Paths ────────────────────

export const DISCOVERY_PATH_SURVEY: PersonaMessages = {
  elementary: '강점과 성장 기회를 발견해요. 너만 볼 수 있는 맞춤 분석!',
  secondary: '강점과 성장 기회를 발견해봐. 너만의 맞춤 분석!',
  university: '강점과 성장 기회를 분석해드려요. 맞춤 전략 포함.',
  professional: '강점과 성장 기회를 분석합니다. 맞춤 전략 포함.',
};

export const DISCOVERY_PATH_GOALS: PersonaMessages = {
  elementary: '유형을 알았으니 바로 실천! 작은 목표부터.',
  secondary: '유형을 알았으니 바로 실천! 작은 목표부터 시작하자.',
  university: '유형을 알았으니 바로 실천으로. 작은 목표부터.',
  professional: '유형을 파악했으니 바로 실천으로. 작은 목표부터.',
};

export const DISCOVERY_PATH_HOME: PersonaMessages = {
  elementary: '어떤 기능이 있는지 먼저 구경! 언제든 돌아올 수 있어.',
  secondary: '어떤 기능이 있는지 먼저 둘러봐. 언제든 돌아올 수 있어.',
  university: '어떤 기능이 있는지 먼저 둘러보세요. 언제든 돌아올 수 있어요.',
  professional: '어떤 기능이 있는지 먼저 둘러보세요. 언제든 돌아올 수 있습니다.',
};

export const DISCOVERY_PATH_SURVEY_CTA: PersonaMessages = {
  elementary: '내 상태 확인하기',
  secondary: '내 상태 확인하기',
  university: '상태 확인하기',
  professional: '상태 확인하기',
};

// ─── Survey ──────────────────────────────────────

export const SURVEY_SUBSCALE_INTRO: Record<Subscale, PersonaMessages> = {
  planning: {
    elementary: '먼저 \'준비하기\'에 대해 물어볼게! 공부하기 전에 어떻게 하는지 알고 싶어.',
    secondary: '첫 번째 영역: 계획. 시작 전에 어떻게 준비하는지 볼게.',
    university: '첫 번째 영역입니다: 계획. 편하게 답변해주세요.',
    professional: '첫 번째 영역: 계획입니다.',
  },
  monitoring: {
    elementary: '잘했어! 🌟 이번엔 \'하는 도중에\' 어떻게 하는지 볼게.',
    secondary: '좋아! 다음은 — 진행하면서 어떻게 점검하는지 볼 거야.',
    university: '다음 영역: 진행하면서 점검하기예요. 조금만 더 힘내주세요!',
    professional: '다음 영역: 실행 점검입니다.',
  },
  evaluation: {
    elementary: '거의 다 왔어! 💪 마지막이야! 끝나고 돌아보는 것에 대해 물어볼게.',
    secondary: '마지막 영역! 끝나면 네 강점이 뭔지 바로 알려줄게.',
    university: '마지막 영역입니다. 끝나면 맞춤 분석 결과를 바로 보여드릴게요.',
    professional: '마지막 영역입니다. 완료 후 분석 리포트를 제공합니다.',
  },
};

export const SURVEY_COUNTDOWN: PersonaMessages = {
  elementary: '3개만 더! 거의 끝이야! 🏁',
  secondary: '3문항 남았어. 끝나면 네 분석 결과 바로 나와!',
  university: '거의 완료입니다. 마지막 3문항이에요.',
  professional: '3문항 남았습니다.',
};

export const SURVEY_COMPLETE_SPEECH: PersonaMessages = {
  elementary: '🎉 대단해! 다 끝냈어!\n네가 답해준 것들을 분석해서 진짜 너만의 비법을 만들어줄게!',
  secondary: '수고했어! ✨ 끝까지 해냈네.\n어디가 강하고 어디를 키우면 좋을지, 바로 분석 결과를 보여줄게.',
  university: '진단이 완료되었어요! 🎯\n응답을 분석해서 맞춤 성장 전략을 준비하고 있어요.',
  professional: '진단이 완료되었습니다.\n맞춤 분석 리포트를 생성하고 있습니다.',
};

// ─── Results → Goals Bridge ──────────────────────

export function getResultsBridgeSpeech(
  persona: Persona,
  weakestSubscale: Subscale,
): string {
  const msgs: Record<Subscale, PersonaMessages> = {
    planning: {
      elementary: '너는 실력이 좋은데, \'준비\'를 좀 더 하면 훨씬 나아질 수 있어!\n매일 시작하기 전에 \'오늘 할 일 3개\'만 적어도 2주면 달라져. 한번 해볼까?',
      secondary: '계획 영역이 성장 기회야. 어렵지 않아!\n매일 시작 전에 할 일 3개만 적어봐. 이 습관 하나로 효율이 확 달라질 수 있어.',
      university: '계획 영역에서 성장 여지가 크네요.\n매일 시작 전 \'핵심 과제 3가지\' 정리만으로도 효율이 크게 달라진다는 연구가 있어요.\n작은 목표 하나, 같이 설정해볼까요?',
      professional: '계획 영역에서 성장 기회가 있습니다.\n매일 시작 시 \'핵심 과제 3가지\' 정리만으로도 효율이 체감됩니다.\n첫 번째 실행 목표를 설정해보시겠습니까?',
    },
    monitoring: {
      elementary: '공부하면서 \'지금 이해하고 있나?\' 확인하는 게 좀 더 필요해!\n30분마다 한 번만 확인해봐. 금방 습관이 될 거야!',
      secondary: '하면서 점검하는 습관이 성장 기회야. 어렵지 않아!\n공부하다가 30분마다 \'이해하고 있나?\' 한 번만 자문하면 돼.\n이 습관 하나로 상위권 패턴에 가까워질 수 있어.',
      university: '진행하면서 점검하는 영역에서 성장 여지가 크네요.\n진행 중 정기 점검 습관 하나가 성과를 크게 바꿀 수 있어요.\n작은 목표 하나, 같이 설정해볼까요?',
      professional: '점검 영역에서 성장 기회가 있습니다.\n진행 중 정기적 점검 습관 하나가 성과를 크게 바꿀 수 있습니다.\n첫 번째 실행 목표를 설정해보시겠습니까?',
    },
    evaluation: {
      elementary: '끝나고 돌아보는 습관을 키우면 훨씬 잘할 수 있어!\n매일 끝나면 \'오늘 뭘 배웠지?\' 한 줄만 적어봐. 쉽지?',
      secondary: '평가 영역이 성장 기회야.\n끝나면 \'잘한 것 하나, 고칠 것 하나\'만 적어봐.\n이 5분 투자로 다음 번이 훨씬 나아질 수 있어.',
      university: '평가 영역에서 성장 여지가 크네요.\n\'주간 복기\' 습관 하나가 장기 기억 정착률을 40% 높인다는 연구가 있어요.\n작은 목표 하나, 같이 설정해볼까요?',
      professional: '평가 영역에서 성장 기회가 있습니다.\n정기적 복기 습관 하나가 장기 성장에 큰 차이를 만듭니다.\n첫 번째 실행 목표를 설정해보시겠습니까?',
    },
  };
  return msgs[weakestSubscale][persona];
}

// ─── My Tests ────────────────────────────────────

export const MY_TESTS_HEADER_HAS_TESTS: PersonaMessages = {
  elementary: '여기서 네가 얼마나 성장했는지 볼 수 있어! 📈',
  secondary: '네 성장 기록이야. 얼마나 달라졌는지 확인해봐!',
  university: '성장 기록이에요. 변화 추이를 확인해보세요.',
  professional: '성장 기록입니다. 변화 추이를 확인하세요.',
};

export const MY_TESTS_HEADER_NO_TESTS: PersonaMessages = {
  elementary: '아직 기록이 없어! 첫 번째 기록을 만들어볼까?\n나중에 여기서 네가 얼마나 성장했는지 볼 수 있어!',
  secondary: '아직 기록이 없어. 첫 진단을 하면 성장 그래프가 시작돼. 시작이 반이야!',
  university: '아직 기록이 없습니다. 첫 진단이 성장 추적의 기준점이 됩니다.',
  professional: '진단 기록이 없습니다. 첫 측정이 변화 추적의 기준선이 됩니다.',
};

export function getGrowthNarrative(
  persona: Persona,
  prevScore: number,
  currScore: number,
  improvedSubscale?: string,
): string {
  const diff = currScore - prevScore;

  if (diff > 0) {
    const msgs: PersonaMessages = {
      elementary: `와! 지난번보다 ${diff}점 올랐어! 🎉 네가 노력한 게 보여!`,
      secondary: `저번 ${prevScore}점 → 이번 ${currScore}점. ${diff}점 성장! 꾸준히 하면 더 올라갈 거야.`,
      university: `이전 대비 ${diff}점 향상.${improvedSubscale ? ` 특히 ${improvedSubscale} 영역의 성장이 눈에 띕니다.` : ''}`,
      professional: `이전 대비 ${diff}점 향상.${improvedSubscale ? ` ${improvedSubscale} 역량이 강화되고 있습니다.` : ''}`,
    };
    return msgs[persona];
  }

  const msgs: PersonaMessages = {
    elementary: '점수가 비슷해도 괜찮아! 꾸준히 하는 것 자체가 대단한 거야.',
    secondary: '변동은 자연스러운 거야. 컨디션에 따라 달라질 수 있거든. 장기 추세를 보자.',
    university: '점수 변동은 자연스럽습니다. 장기 추세가 중요해요.',
    professional: '단기 변동보다 장기 추세가 중요합니다. 꾸준한 측정 자체가 성장의 신호입니다.',
  };
  return msgs[persona];
}

export const MY_TESTS_INFO_BOX: PersonaMessages = {
  elementary: '정기적으로 측정하면 나만의 성장 그래프가 만들어져!\n추천 주기: 2~4주마다 한 번.',
  secondary: '정기적으로 측정하면 나만의 성장 그래프가 만들어져.\n추천 주기: 2~4주마다 한 번.',
  university: '정기적으로 측정하면 나만의 성장 그래프가 만들어져요.\n추천 주기: 2~4주마다 한 번.',
  professional: '정기적으로 측정하면 성장 그래프가 만들어집니다.\n추천 주기: 2~4주마다 한 번.',
};

// ─── Timer Running Encouragement ─────────────────

export const TIMER_ENCOURAGEMENT: Record<'early' | 'mid' | 'final', PersonaMessages> = {
  early: {
    elementary: '좋아, 집중 시작! 넌 할 수 있어! 💪',
    secondary: '집중 모드 ON! 이 시간은 오직 너를 위한 시간이야.',
    university: '집중 모드 시작. 이 시간에 온전히 몰입해보세요.',
    professional: '집중 모드 시작. 깊은 몰입의 시간입니다.',
  },
  mid: {
    elementary: '잘하고 있어! 절반 넘었다! ⭐',
    secondary: '절반 지났어! 이 흐름 유지하자.',
    university: '절반을 지났습니다. 좋은 흐름이에요.',
    professional: '절반을 지났습니다. 흐름을 유지하세요.',
  },
  final: {
    elementary: '거의 다 왔어! 마지막까지 화이팅! 🏁',
    secondary: '거의 끝! 마지막까지 밀어붙이자!',
    university: '곧 완료됩니다. 마무리까지 힘내세요!',
    professional: '곧 완료됩니다. 마무리까지.',
  },
};

// ─── Round-robin variant selection ───────────────

const variantCounters = new Map<string, number>();

/** Pick next variant in a round-robin fashion for a given situation key */
export function pickVariant<T>(key: string, variants: T[]): T {
  if (variants.length === 0) return variants[0];
  const current = variantCounters.get(key) ?? 0;
  const idx = current % variants.length;
  variantCounters.set(key, current + 1);
  return variants[idx];
}

// ─── Persona card descriptions (onboarding) ─────

export const PERSONA_CARD_DESCRIPTIONS: PersonaMessagesArray = {
  elementary: ['함께 공부 습관을 알아보고, 더 재미있게 성장해요!'],
  secondary: ['나만의 학습 스타일을 찾고, 효율적으로 공부할 수 있어요.'],
  university: ['성장 패턴을 분석하고 나에게 맞는 전략을 만들어요.'],
  professional: ['나만의 성장 루틴을 만들어보세요.'],
};

// ─── Checkin ─────────────────────────────────

export const CHECKIN_MORNING_INTRO: PersonaMessages = {
  elementary: '좋은 아침! ☀️ 오늘은 어떤 하루가 될까?\n같이 오늘의 계획을 세워보자!',
  secondary: '좋은 아침! 오늘의 컨디션은 어때?\n3분만 투자하면 하루가 달라져.',
  university: '좋은 아침이에요. 오늘 하루의 방향을 잡아볼까요?\n짧은 계획이 하루의 집중력을 바꿉니다.',
  professional: '좋은 아침입니다. 오늘 하루의 방향을 설정해보세요.\n잠깐의 정리가 하루의 효율을 높여줍니다.',
};

export const CHECKIN_EVENING_INTRO: PersonaMessages = {
  elementary: '오늘 하루 수고했어! 🌙\n어떤 하루였는지 같이 돌아보자.',
  secondary: '오늘 하루 수고했어.\n잠깐 돌아보면 내일이 더 좋아지거든.',
  university: '오늘 하루 수고하셨어요.\n잠깐의 복기가 내일의 성장으로 이어져요.',
  professional: '오늘 하루 수고하셨습니다.\n짧은 복기 한 번이 내일의 효율을 높여줍니다.',
};

// ─── Goals ───────────────────────────────────

export const GOALS_PAGE_INTRO: PersonaMessages = {
  elementary: '목표를 정하면 꿈에 한 발짝 가까워져! 🎯\n작은 것부터 시작해보자.',
  secondary: '목표가 있으면 방향이 생겨.\n크지 않아도 괜찮아, 지금 할 수 있는 것부터!',
  university: '목표를 구체적으로 정하면 실천 확률이 높아져요.\n작은 행동 하나부터 시작해보세요.',
  professional: '구체적인 목표 설정이 실행력의 시작입니다.\n작은 행동 하나부터 설정해보세요.',
};

// ─── Pulse ───────────────────────────────────

export const PULSE_PAGE_INTRO: PersonaMessages = {
  elementary: '잠깐! 지금 기분이 어떤지 체크해볼까? ⚡\n30초면 돼!',
  secondary: '잠깐만! 지금 에너지 상태 한번 확인해봐.\n컨디션에 맞춰 전략을 바꾸면 효율이 올라가거든.',
  university: '잠깐 에너지 상태를 확인해보세요.\n지금의 컨디션을 알면 남은 시간을 더 잘 쓸 수 있어요.',
  professional: '잠깐 에너지 상태를 점검해보세요.\n현재 컨디션에 맞는 전략이 효율을 바꿔줍니다.',
};

// ─── Growth Moment ───────────────────────────

export const GROWTH_MOMENT_INTRO_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '오늘 하루 수고했어! 💡\n잠깐, 오늘 있었던 일 같이 얘기해볼까?',
    '하루가 끝나가고 있어! 오늘 어떤 하루였어?\n편하게 적어봐.',
    '여기 와줬구나 😊\n1분만 같이 오늘을 돌아보자!',
  ],
  secondary: [
    '오늘 하루 수고했어.\n잠깐, 오늘을 돌아보는 시간을 가져볼까?',
    '하루를 마무리하는 중이구나.\n오늘 어떤 하루였는지, 가볍게 적어봐.',
    '잠깐 쉬면서 오늘을 돌아보자.\n1분이면 충분해.',
  ],
  university: [
    '오늘 하루 수고하셨어요.\n잠깐, 오늘을 돌아보는 시간을 가져볼까요?',
    '하루를 마무리하는 중이군요.\n오늘 어떤 하루였는지, 가볍게 적어봐요.',
    '잠깐 멈추고 오늘을 돌아봐요.\n1분이면 충분해요.',
  ],
  professional: [
    '오늘 하루 수고하셨어요.\n잠깐, 오늘을 돌아보는 시간을 가져볼까요?',
    '하루를 마무리하는 중이군요.\n오늘 어떤 하루였는지, 가볍게 적어봐요.',
    '잠깐 멈추고 오늘을 돌아봐요.\n1분이면 충분합니다.',
  ],
};

export function getGrowthMomentIntro(persona: Persona): string {
  return pickVariant(`growth_moment_intro_${persona}`, GROWTH_MOMENT_INTRO_VARIANTS[persona]);
}

// ─── Profile/Growth ──────────────────────────

export const GROWTH_PROFILE_INTRO: PersonaMessages = {
  elementary: '여기서 네가 얼마나 성장했는지 한눈에 볼 수 있어! 📊\n어떤 게 강한지 같이 보자.',
  secondary: '네 강점과 성장 포인트를 한눈에 확인해봐.\n어디가 강하고 어디를 키우면 좋을지 보여줄게.',
  university: '5가지 영역에서 현재 위치를 확인해보세요.\n강점을 기반으로 성장 전략을 세울 수 있어요.',
  professional: '5가지 핵심 영역의 현재 수준을 확인하세요.\n강점을 기반으로 성장 방향을 설계할 수 있습니다.',
};

// ─── Profile/Weekly Report ───────────────────

export const WEEKLY_REPORT_INTRO: PersonaMessages = {
  elementary: '이번 주에 어떤 일이 있었는지 정리해봤어! 📈\n같이 확인해보자.',
  secondary: '이번 주 활동을 분석해봤어.\n어떤 패턴이 있는지 확인해봐!',
  university: '이번 주 활동 데이터를 분석했어요.\n패턴을 알면 다음 주가 더 효율적이에요.',
  professional: '이번 주 활동 데이터를 정리했습니다.\n패턴을 확인하고 다음 주 전략을 세워보세요.',
};

// ─── Dashboard Welcome (round-robin variants) ──

export const DASHBOARD_WELCOME_VARIANTS: PersonaMessagesArray = {
  // 전략: 목표확인 / 어제연결 / 에너지체크 / 성취인정 / 호기심 / 패턴 / 새시도 / 강점 / 여유허용 / 격려
  elementary: [
    '안녕! 오늘은 뭘 해보고 싶어?\n같이 정해보자 😊',
    '어제 했던 거 기억나? 거기서 이어가볼까!\n아니면 새로운 것도 좋아.',
    '오늘 기분은 어때? 😊\n기분에 맞춰서 오늘 할 일을 정해보자.',
    '여기 꾸준히 오는 거 자체가 대단해!\n오늘은 뭘 도전해볼까?',
    '오늘은 뭔가 새로운 걸 발견할 수 있을지도!\n같이 살펴볼까?',
    '최근에 점점 잘하고 있는 거 알아?\n이 조자로 오늘도 가보자!',
    '오늘은 평소랑 다른 것도 해볼까?\n가끔은 새로운 게 재미있어.',
    '네가 잘하는 거 있잖아.\n그걸 오늘도 활용해보면 어때?',
    '오늘 좀 쉬고 싶으면 쉬어도 괜찮아.\n가볍게 하나만 해보는 건 어때?',
    '한 걸음씩 가는 거야.\n오늘의 그 한 걸음, 같이 정해볼까?',
  ],
  secondary: [
    '오늘의 핵심 한 가지를 정해볼까?\n전부 다 하려고 하지 않아도 돼.',
    '어제 한 것 중에 이어갈 만한 게 있어?\n연속성이 생각보다 큰 힘이 돼.',
    '오늘 에너지는 어때?\n컨디션에 맞게 속도를 조절하는 것도 실력이야.',
    '꾸준히 들어오고 있네. 이게 쉬운 게 아닌데.\n그 자체로 성장하고 있는 거야.',
    '오늘은 뭐가 궁금해?\n궁금한 게 있으면 거기서부터 시작해봐.',
    '최근 흐름이 좋아. 네가 만든 루틴이 작동하고 있어.\n오늘도 그 흐름을 이어가볼까?',
    '오늘은 평소랑 다른 걸 시도해봐도 좋아.\n익숙한 것만 하면 성장이 멈추거든.',
    '너 ○○ 잘하잖아. 오늘도 그 강점을 써먹어봐.\n강점 위에 쌓는 게 가장 빠른 길이야.',
    '오늘 좀 지치면, 가볍게만 해도 충분해.\n매일 전력질주할 필요 없어.',
    '어제보다 조금만 더. 그게 복리로 쌓여.\n오늘의 한 걸음은 뭘로 할까?',
  ],
  university: [
    '오늘의 핵심 한 가지를 정해볼까요?\n전부 다 하려고 하지 않아도 괜찮아요.',
    '어제 하던 것에서 이어가볼까요?\n연속성이 효율을 만들어요.',
    '오늘의 컨디션은 어때요?\n컨디션에 맞게 계획을 조절하는 것도 전략이에요.',
    '꾸준히 들어오시는 거 자체가 성장이에요.\n오늘은 어디서부터 시작할까요?',
    '오늘 새롭게 궁금한 게 있나요?\n호기심에서 시작하는 하루가 가장 생산적이에요.',
    '최근 흐름을 보면 점점 자리 잡아가고 있어요.\n이 루틴을 이어가볼까요?',
    '가끔은 새로운 접근을 시도해보는 것도 좋아요.\n익숙함 밖에서 발견하는 게 있거든요.',
    '본인의 강점을 오늘도 활용해보세요.\n강점 위에 쌓는 성장이 가장 단단해요.',
    '오늘 좀 지치셨으면, 가볍게만 해도 충분해요.\n쉬는 것도 루틴의 일부예요.',
    '어제보다 1%만 더. 작아 보여도 복리로 쌓여요.\n오늘의 한 걸음은 뭘로 할까요?',
  ],
  professional: [
    '오늘의 핵심 한 가지를 정해볼까요?\n모든 걸 다 할 필요 없습니다.',
    '어제 하던 것에서 이어가볼까요?\n흐름을 유지하는 게 가장 효율적입니다.',
    '오늘의 컨디션은 어떠세요?\n컨디션에 맞게 조절하는 것도 전략입니다.',
    '꾸준히 들어오시는 것 자체가 의미 있습니다.\n오늘은 어디서부터 시작할까요?',
    '오늘 새롭게 시도해보고 싶은 게 있나요?\n작은 실험이 큰 전환점이 되기도 합니다.',
    '최근 흐름을 보면 점점 안정되고 있어요.\n이 루틴을 계속 이어가볼까요?',
    '가끔은 다른 접근을 시도해보는 것도 좋습니다.\n새로운 관점이 돌파구가 될 수 있어요.',
    '본인의 강점을 오늘도 활용해보세요.\n강점 기반의 성장이 가장 지속 가능합니다.',
    '오늘 좀 지치셨으면, 가볍게 하나만 해도 충분합니다.\n회복도 성장의 일부예요.',
    '어제보다 1%만 더. 작아 보여도 복리로 쌓입니다.\n오늘의 한 걸음은 뭘로 할까요?',
  ],
};

export function getDashboardWelcome(persona: Persona): string {
  return pickVariant(`dashboard_welcome_${persona}`, DASHBOARD_WELCOME_VARIANTS[persona]);
}

// ─── Checkin Morning Variants (round-robin) ─────

export const CHECKIN_MORNING_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '좋은 아침! ☀️ 오늘은 어떤 하루가 될까?\n같이 오늘의 계획을 세워보자!',
    '일어났구나! 오늘도 좋은 하루 만들자!\n먼저 오늘 할 일을 생각해볼까?',
    '좋은 아침! 오늘은 뭘 해보고 싶어?\n같이 정해보자!',
  ],
  secondary: [
    '좋은 아침! 오늘의 컨디션은 어때?\n3분만 투자하면 하루가 달라져.',
    '아침이야! 어제 자기 전에 생각한 거 있어?\n오늘 실천해보자.',
    '좋은 아침! 오늘 하루 어떻게 보낼지\n잠깐 정리해볼까?',
  ],
  university: [
    '좋은 아침이에요. 오늘 하루의 방향을 잡아볼까요?\n짧은 계획이 하루의 집중력을 바꿉니다.',
    '좋은 아침이에요. 오늘의 핵심 과제는 무엇인가요?\n하나만 정해도 효율이 달라져요.',
    '좋은 아침이에요. 어제의 경험을 오늘에 반영해볼까요?\n성장은 매일의 작은 조정에서 옵니다.',
  ],
  professional: [
    '좋은 아침입니다. 오늘 하루의 방향을 설정해보세요.\n잠깐의 정리가 하루의 효율을 높여줍니다.',
    '좋은 아침입니다. 오늘의 우선순위 3가지를 정해보세요.\n명확한 방향이 집중력을 만듭니다.',
    '좋은 아침입니다. 오늘 가장 중요한 과제는 무엇인가요?\n핵심에 집중하면 결과가 달라집니다.',
  ],
};

export function getCheckinMorning(persona: Persona): string {
  return pickVariant(`checkin_morning_${persona}`, CHECKIN_MORNING_VARIANTS[persona]);
}

// ─── Checkin Evening Variants (round-robin) ─────

export const CHECKIN_EVENING_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '오늘 하루 수고했어! 🌙\n어떤 하루였는지 같이 돌아보자.',
    '오늘도 잘했어! 🌙\n뭐가 제일 재미있었어?',
    '수고했어! 오늘 하루 어땠어?\n같이 정리해보자!',
  ],
  secondary: [
    '오늘 하루 수고했어.\n잠깐 돌아보면 내일이 더 좋아지거든.',
    '오늘 수고했어! 잘한 건 뭐였어?\n하나만 떠올려봐.',
    '하루가 끝났네! 오늘 배운 게 있어?\n한 줄이라도 적어보자.',
  ],
  university: [
    '오늘 하루 수고하셨어요.\n잠깐의 복기가 내일의 성장으로 이어져요.',
    '수고하셨어요. 오늘의 성과를 정리해볼까요?\n기록이 쌓이면 패턴이 보여요.',
    '하루를 마무리하며, 오늘의 배움을 정리해보세요.\n작은 성찰이 큰 차이를 만들어요.',
  ],
  professional: [
    '오늘 하루 수고하셨습니다.\n짧은 복기 한 번이 내일의 효율을 높여줍니다.',
    '수고하셨습니다. 오늘의 핵심 성과를 정리해보세요.\n기록이 곧 자산이 됩니다.',
    '하루를 마무리하며 점검해보세요.\n지속적인 복기가 성장의 핵심입니다.',
  ],
};

export function getCheckinEvening(persona: Persona): string {
  return pickVariant(`checkin_evening_${persona}`, CHECKIN_EVENING_VARIANTS[persona]);
}

// ─── Goals Page Variants (round-robin) ──────────

export const GOALS_PAGE_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '목표를 정하면 꿈에 한 발짝 가까워져! 🎯\n작은 것부터 시작해보자.',
    '오늘은 어떤 목표를 정해볼까?\n작은 것도 괜찮아! 시작이 중요해.',
    '목표가 있으면 힘이 나! 🎯\n같이 정해보자!',
  ],
  secondary: [
    '목표가 있으면 방향이 생겨.\n크지 않아도 괜찮아, 지금 할 수 있는 것부터!',
    '목표를 세우면 뭘 해야 할지 명확해져.\n오늘 하나만 정해볼까?',
    '작은 목표 하나가 큰 변화의 시작이야.\n뭘 해보고 싶어?',
  ],
  university: [
    '목표를 구체적으로 정하면 실천 확률이 높아져요.\n작은 행동 하나부터 시작해보세요.',
    '구체적인 목표일수록 실행력이 올라가요.\n오늘 하나만 추가해볼까요?',
    '목표를 시각화하면 동기가 유지돼요.\n지금 할 수 있는 것부터 정해보세요.',
  ],
  professional: [
    '구체적인 목표 설정이 실행력의 시작입니다.\n작은 행동 하나부터 설정해보세요.',
    '목표가 명확할수록 성과가 달라집니다.\n오늘의 실행 과제를 정해보세요.',
    '작은 목표의 달성이 큰 성장의 기반입니다.\n하나씩 완료해나가세요.',
  ],
};

export function getGoalsPageIntro(persona: Persona): string {
  return pickVariant(`goals_page_${persona}`, GOALS_PAGE_VARIANTS[persona]);
}

// ─── Timer Header Variants (round-robin) ────────

export const TIMER_HEADER_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '집중할 시간이야! ⏱️\n타이머를 맞추고 시작해보자!',
    '준비됐어? 집중 모드 ON! ⏱️\n네 속도로 하면 돼!',
    '타이머 준비! 집중하면 놀라운 일이 생겨! ⏱️',
  ],
  secondary: [
    '집중 시간이야. 타이머 설정하고 시작하자!\n짧게라도 집중하면 효과가 확실해.',
    '타이머 준비! 집중과 휴식의 밸런스가 핵심이야.\n시간을 정하고 시작해볼까?',
    '집중할 준비됐어? 타이머를 활용하면\n시간 감각이 좋아지거든.',
  ],
  university: [
    '집중 시간을 설정해보세요.\n타이머를 활용하면 몰입도가 달라집니다.',
    '시간을 정하고 집중해보세요.\n집중과 휴식의 리듬이 효율을 만들어요.',
    '타이머를 활용한 집중 세션을 시작해보세요.\n시간 관리가 성과의 기반이에요.',
  ],
  professional: [
    '집중 세션을 설정해보세요.\n시간을 구조화하면 효율이 달라집니다.',
    '타이머를 활용해 집중 시간을 관리해보세요.\n시간 블록 관리가 생산성의 핵심입니다.',
    '집중 세션을 시작해보세요.\n구조화된 시간이 최상의 성과를 만듭니다.',
  ],
};

export function getTimerHeader(persona: Persona): string {
  return pickVariant(`timer_header_${persona}`, TIMER_HEADER_VARIANTS[persona]);
}

// ─── My Tests Header Variants (round-robin, 3개로 확장) ──

export const MY_TESTS_HEADER_VARIANTS: PersonaMessagesArray = {
  elementary: [
    '여기서 네가 얼마나 성장했는지 볼 수 있어! 📈',
    '네 기록이 여기 다 있어! 얼마나 달라졌는지 볼까? 📈',
    '성장 기록을 확인해보자! 점점 나아지고 있어 📈',
  ],
  secondary: [
    '네 성장 기록이야. 얼마나 달라졌는지 확인해봐!',
    '기록을 보면 패턴이 보여. 어디가 성장했는지 확인해봐!',
    '꾸준히 측정하면 변화가 눈에 보여. 확인해볼까?',
  ],
  university: [
    '성장 기록이에요. 변화 추이를 확인해보세요.',
    '측정 기록이 쌓이면 패턴이 보여요. 추이를 확인해보세요.',
    '정기적 측정의 힘! 변화의 흐름을 확인해보세요.',
  ],
  professional: [
    '성장 기록입니다. 변화 추이를 확인하세요.',
    '측정 데이터가 쌓이고 있습니다. 성장 패턴을 확인해보세요.',
    '꾸준한 측정이 가장 확실한 성장 지표입니다.',
  ],
};

export function getMyTestsHeader(persona: Persona, hasTests: boolean): string {
  if (!hasTests) return MY_TESTS_HEADER_NO_TESTS[persona];
  return pickVariant(`my_tests_header_${persona}`, MY_TESTS_HEADER_VARIANTS[persona]);
}

// ─── Dashboard (logged-in home) ─────────────────

export const DASHBOARD_LOGGED_IN: PersonaMessages = {
  elementary: '돌아왔구나! 오늘도 같이 성장해보자 😊\n어디부터 시작할까?',
  secondary: '돌아왔네! 오늘은 뭘 해볼까?\n루틴을 유지하는 것만으로도 대단해.',
  university: '돌아오셨네요. 오늘의 목표를 확인해볼까요?\n꾸준함이 가장 강력한 전략이에요.',
  professional: '돌아오셨습니다. 오늘의 우선순위를 확인해보세요.\n꾸준한 루틴이 성과를 만듭니다.',
};

// ─── Achievements ───────────────────────────────

export const ACHIEVEMENTS_INTRO: PersonaMessages = {
  elementary: '와, 여기까지 왔어! 🏆\n네가 모은 배지들을 확인해보자!\n다음 배지도 곧 딸 수 있을 거야!',
  secondary: '네가 달성한 것들이야! 🏆\n하나하나 쌓이면 큰 성장이 돼.\n다음 목표도 확인해봐!',
  university: '달성한 업적을 확인해보세요.\n작은 성취가 모여 큰 성장이 됩니다.\n다음 목표를 향해 한 걸음 더!',
  professional: '달성 현황을 확인하세요.\n꾸준한 실행의 증거가 쌓이고 있습니다.\n다음 목표를 확인해보세요.',
};

// ─── Coach ──────────────────────────────────────

export const COACH_PAGE_INTRO: PersonaMessages = {
  elementary: '궁금한 거 있어? 뭐든 편하게 물어봐! 💬\n같이 생각해보자.',
  secondary: '뭐든 물어봐! 공부 전략이든, 고민이든.\n같이 방법을 찾아보자.',
  university: '편하게 질문해주세요.\n성장 전략, 시간 관리, 동기부여 — 무엇이든 도와드릴게요.',
  professional: '무엇이든 질문해주세요.\n목표 설정, 루틴 관리, 성장 전략 — 함께 방법을 찾아드립니다.',
};

// ─── Auth / Login ───────────────────────────────

export const AUTH_LOGIN_WELCOME: PersonaMessages = {
  elementary: '안녕! 반가워 😊\n아이디를 넣고 들어오자!',
  secondary: '반가워! 로그인하고 시작하자.',
  university: '반갑습니다.\n로그인 후 시작하세요.',
  professional: '반갑습니다.\n로그인 후 시작하세요.',
};

export const AUTH_LOGIN_WELCOME_RETURN: PersonaMessages = {
  elementary: '다시 만나서 반가워! 😊\n아이디를 넣고 들어오자!',
  secondary: '다시 왔구나! 반가워.\n로그인하고 이어서 하자.',
  university: '다시 방문해주셨네요. 반갑습니다.\n로그인 후 이어서 진행하세요.',
  professional: '다시 방문해주셨습니다. 반갑습니다.\n로그인 후 이어서 진행하세요.',
};

// ─── Auth / Register ────────────────────────────

export const AUTH_REGISTER_WELCOME: PersonaMessages = {
  elementary: '함께 시작하자! ✨\n같이 공부하고 성장할 준비됐어?',
  secondary: '환영해! 함께 성장할 준비됐어?\n가입하면 나만의 성장 기록이 시작돼.',
  university: '환영합니다! 가입하시면 맞춤 분석과\n성장 기록을 제공해드려요.',
  professional: '환영합니다. 가입하시면 맞춤 분석과\n성장 기록 서비스를 이용하실 수 있습니다.',
};

// ─── Student / Join Group ───────────────────────

export const STUDENT_JOIN_GROUP: PersonaMessages = {
  elementary: '친구들이랑 같이 하면 더 재밌어! 🤝\n그룹 코드를 넣어봐!',
  secondary: '함께하면 더 재밌어! 그룹 코드를 입력하면\n같이 성장할 수 있어.',
  university: '그룹에 참여하면 함께 성장할 수 있어요.\n초대 코드를 입력해주세요.',
  professional: '그룹에 참여하면 함께 성장할 수 있습니다.\n초대 코드를 입력해주세요.',
};

// ─── Student / Parent Connection ────────────────

export const STUDENT_PARENT_CONNECTION: PersonaMessages = {
  elementary: '엄마 아빠도 네 성장을 같이 볼 수 있어! 👨‍👩‍👧\n연결하면 함께 응원해줄 거야.',
  secondary: '보호자를 연결하면 네 성장을 함께 확인할 수 있어.\n원하면 연결해봐!',
  university: '보호자를 연결하시면 성장 과정을 공유할 수 있어요.\n원하시면 연결해보세요.',
  professional: '보호자 연결 기능입니다.\n성장 과정을 공유하고 싶으시면 연결해보세요.',
};

// ─── Student / Group Requests ───────────────────

export const STUDENT_GROUP_REQUESTS: PersonaMessages = {
  elementary: '그룹 신청 상태를 확인해볼까? ⏳\n조금만 기다리면 돼!',
  secondary: '그룹 가입 상태를 확인해봐.\n승인되면 바로 알려줄게!',
  university: '그룹 가입 요청 상태를 확인해보세요.\n승인되면 알림을 보내드릴게요.',
  professional: '그룹 가입 요청 상태를 확인하세요.\n승인 시 알림을 보내드립니다.',
};

// ─── Groups / Join ──────────────────────────────

export const GROUPS_JOIN_INTRO: PersonaMessages = {
  elementary: '새 그룹에 들어가볼까? 🎒\n코드를 넣으면 바로 참여할 수 있어!',
  secondary: '그룹에 참여하면 함께 성장할 수 있어.\n초대 코드를 입력해봐!',
  university: '그룹에 참여해보세요.\n함께하면 동기부여가 달라져요.',
  professional: '그룹 참여를 통해 함께 성장하세요.\n초대 코드를 입력해주세요.',
};

// ─── Parent ─────────────────────────────────────

export const PARENT_PAGE_INTRO: PersonaMessages = {
  elementary: '자녀의 성장을 함께 지켜봐요! 👪\n여기서 자녀의 활동을 확인할 수 있어요.',
  secondary: '자녀의 성장을 함께 지켜봐요! 👪\n여기서 자녀의 활동을 확인할 수 있어요.',
  university: '자녀의 성장 과정을 함께 확인해보세요.\n활동 현황과 성장 추이를 제공해드려요.',
  professional: '자녀의 성장 과정을 확인하세요.\n활동 현황과 성장 추이를 제공합니다.',
};

// ─── Parent / Children ──────────────────────────

export const PARENT_CHILDREN_INTRO: PersonaMessages = {
  elementary: '자녀의 현황을 한눈에 볼 수 있어요! 📋\n어떻게 성장하고 있는지 같이 확인해봐요.',
  secondary: '자녀의 현황을 한눈에 볼 수 있어요! 📋\n어떻게 성장하고 있는지 같이 확인해봐요.',
  university: '자녀의 활동 현황을 확인해보세요.\n성장 과정을 함께 지켜보는 것이 큰 힘이 됩니다.',
  professional: '자녀의 활동 현황을 확인하세요.\n성장 과정을 함께 지켜보는 것이 큰 힘이 됩니다.',
};

// ─── Settings / Profile ─────────────────────────

export const SETTINGS_PROFILE_INTRO: PersonaMessages = {
  elementary: '내 정보를 확인하고 바꿀 수 있어! ⚙️\n맞는지 같이 확인해보자.',
  secondary: '프로필을 확인하고 업데이트할 수 있어.\n맞는 정보인지 확인해봐!',
  university: '프로필 설정을 확인하고 업데이트해보세요.\n정확한 정보가 맞춤 서비스의 기반이에요.',
  professional: '프로필 설정을 확인해주세요.\n정확한 정보가 맞춤 서비스의 기반입니다.',
};

// ─── My Groups ──────────────────────────────────

export const MY_GROUPS_INTRO: PersonaMessages = {
  elementary: '친구들이랑 같은 그룹이면 더 재밌어! 👥\n내 그룹을 확인해보자.',
  secondary: '그룹 활동은 함께 성장하는 힘이야.\n어떤 그룹에 속해있는지 확인해봐!',
  university: '소속 그룹을 확인해보세요.\n함께하면 동기부여가 달라져요.',
  professional: '소속 그룹을 확인하세요.\n함께 성장하는 커뮤니티가 큰 힘이 됩니다.',
};

// ─── Dashboard Empty State ──────────────────────

export const DASHBOARD_EMPTY_STATE: PersonaMessages = {
  elementary: '아직 첫 기록이 없어! 🌱\n첫 검사를 하면 네가 어떤 스타일인지\n알 수 있어. 같이 시작해볼까?',
  secondary: '아직 기록이 없어. 🌱\n첫 검사를 하면 성장 그래프가 시작돼.\n시작이 반이야!',
  university: '아직 검사 기록이 없어요. 🌱\n첫 검사가 성장 추적의 기준점이 됩니다.\n지금 시작해보세요!',
  professional: '검사 기록이 없습니다. 🌱\n첫 측정이 변화 추적의 기준선이 됩니다.\n지금 시작해보세요.',
};

// ─── Profile (Data Management) ──────────────────

export const PROFILE_PAGE_INTRO: PersonaMessages = {
  elementary: '여기서 내 데이터를 관리할 수 있어! 📁\n궁금한 거 있으면 물어봐.',
  secondary: '내 데이터를 여기서 관리할 수 있어.\n필요한 거 확인해봐!',
  university: '데이터 관리 페이지예요.\n내 정보와 기록을 확인하고 관리할 수 있어요.',
  professional: '데이터 관리 페이지입니다.\n정보와 기록을 확인하고 관리하세요.',
};

// ─── Persona Change Confirmation (after login) ──

export const PERSONA_CHANGE_DETECTED: PersonaMessages = {
  elementary: '다시 와줘서 고마워! 😊\n전에는 다른 모드로 했었는데,\n지금도 그 모드가 맞아?',
  secondary: '다시 왔구나! 반가워.\n전에 설정한 모드가 지금도 맞는지 확인하고 싶어.',
  university: '다시 방문해주셨네요.\n이전에 설정한 모드가 현재 상황에 맞는지 확인해볼까요?',
  professional: '다시 방문해주셨습니다.\n이전에 설정한 모드가 현재 상황에 맞는지 확인해보겠습니다.',
};

export const PERSONA_CHANGE_KEEP: PersonaMessages = {
  elementary: '좋아! 그대로 가자! 🎉',
  secondary: '알겠어! 그대로 이어서 하자.',
  university: '네, 그대로 이어서 진행할게요.',
  professional: '네, 기존 설정으로 이어서 진행하겠습니다.',
};

export const PERSONA_CHANGE_SWITCH: PersonaMessages = {
  elementary: '그래, 바꿔보자! 새로운 모드로 해볼까? 🌈',
  secondary: '좋아, 새로운 모드로 바꿔보자!',
  university: '새로운 모드로 변경할게요. 다시 설정해볼까요?',
  professional: '모드를 변경하겠습니다. 다시 설정해볼까요?',
};

// ─── Failure Cause Analysis (evening check-in: 0 achievement) ──

export const FAILURE_ANALYSIS_MESSAGES: PersonaMessagesArray = {
  elementary: [
    '오늘 좀 어려웠나봐. 무엇 때문이었을까?\n같이 생각해보자 — 내일은 더 좋은 방법을 찾을 수 있어!',
    '괜찮아, 이런 날도 있어! 😊\n오늘 뭐가 제일 힘들었어? 알면 다음엔 더 쉬워질 거야.',
    '오늘은 좀 힘든 하루였구나.\n어떤 부분이 어려웠는지 한번 떠올려볼까? 방법을 같이 찾아보자!',
  ],
  secondary: [
    '오늘 계획대로 안 됐어? 어떤 부분이 걸렸는지 한번 생각해볼까.\n원인을 알면 다음에 전략을 바꿀 수 있거든.',
    '이런 날도 있어. 중요한 건 왜 안 됐는지 잠깐 돌아보는 거야.\n시간? 집중? 방법? 어디가 걸렸을까?',
    '계획대로 안 된 날이야. 그런데 이걸 돌아보는 것 자체가 성장이야.\n어떤 점이 방해가 됐는지 생각해봐.',
  ],
  university: [
    '오늘 달성이 어려웠군요. 무엇이 방해가 됐을지 잠깐 돌아봐요.\n원인을 파악하면 내일의 계획이 더 현실적이 됩니다.',
    '계획과 현실 사이에 간극이 있었던 하루네요.\n어떤 요인이 있었는지 정리해보면, 다음번엔 더 나은 전략을 세울 수 있어요.',
    '오늘은 계획대로 흘러가지 않았네요.\n잠깐 돌아보는 시간을 가져볼까요? 이 과정 자체가 성장의 일부예요.',
  ],
  professional: [
    '오늘은 계획대로 흘러가지 않았군요. 어떤 요인이 있었을까요?\n원인을 파악하는 것이 다음 단계의 시작입니다.',
    '계획과 실행 사이에 간극이 있었던 하루입니다.\n어떤 방해 요소가 있었는지 짧게 정리해보시면, 내일의 전략이 달라집니다.',
    '오늘 목표 달성이 어려웠군요.\n잠깐의 복기가 내일의 실행력을 바꿔줍니다. 어떤 부분이 걸렸을까요?',
  ],
};

export function getFailureAnalysis(persona: Persona): string {
  return pickVariant(`failure_analysis_${persona}`, FAILURE_ANALYSIS_MESSAGES[persona]);
}

// ─── Streak Recovery (round-robin, 3+ variants per persona) ─

export const STREAK_RECOVERY_MESSAGES: PersonaMessagesArray = {
  elementary: [
    '며칠 쉬었어도 괜찮아! 다시 시작한 게 제일 중요해! 💪',
    '돌아왔구나! 쉬는 것도 필요한 거야. 이제 다시 같이 해보자!',
    '다시 왔네! 반가워 😊 오늘부터 하나씩 해보자. 넌 할 수 있어!',
  ],
  secondary: [
    '며칠 쉬었어도 전혀 늦지 않았어. 다시 시작하는 게 진짜 실력이야.',
    '돌아온 것만으로 대단해! 꾸준함보다 중요한 건 포기하지 않는 거거든.',
    '쉬었다 돌아오는 것도 전략이야. 오늘부터 다시 흐름을 잡아보자!',
  ],
  university: [
    '잠시 멈추었던 시간도 성장의 일부예요. 다시 시작해볼까요?',
    '돌아오신 것 자체가 동기의 증거입니다. 오늘부터 다시 한 걸음씩!',
    '쉬어간 시간이 있어도 괜찮아요. 중요한 건 다시 돌아왔다는 거예요.',
  ],
  professional: [
    '잠시 멈추었던 시간도 성장의 일부입니다. 다시 시작해볼까요?',
    '돌아오신 것 자체가 의지의 증거입니다. 오늘부터 다시 루틴을 잡아보세요.',
    '쉬어가는 것도 전략입니다. 중요한 건 방향을 잃지 않는 것이고, 지금 바로 그걸 하고 계세요.',
  ],
};

export function getStreakRecoveryMessage(persona: Persona): string {
  return pickVariant(`streak_recovery_${persona}`, STREAK_RECOVERY_MESSAGES[persona]);
}

// ── Badge Interpretation Messages (persona-aware) ──────────────────────────

export const BADGE_INTERPRETATION_MESSAGES: Record<
  'few' | 'some' | 'many',
  Record<Persona, string>
> = {
  few: {
    elementary: '첫 걸음이야! 이 시작이 제일 중요해!',
    secondary: '첫 발을 내디뎠어! 이 시작이 중요해.',
    university: '첫 걸음을 내딛었어요. 이 시작이 중요합니다.',
    professional: '첫 걸음을 내디뎠습니다. 이 시작이 의미 있어요.',
  },
  some: {
    elementary: '와, 꾸준히 하고 있네! 이 흐름 계속 가자!',
    secondary: '꾸준함이 보여! 이 흐름, 이어가자.',
    university: '꾸준함이 보여요. 이 흐름을 이어가봐요.',
    professional: '꾸준함이 보입니다. 이 흐름을 이어가세요.',
  },
  many: {
    elementary: '대단해! 이렇게 많이 모았어! 정말 자랑스러워!',
    secondary: '대단한 여정이야! 지금까지의 성장, 자랑스러워해도 돼.',
    university: '대단한 여정이에요. 지금까지의 성장을 자랑스러워하세요.',
    professional: '인상적인 여정입니다. 이 꾸준함이 성장의 핵심이에요.',
  },
};

export function getBadgeInterpretation(persona: Persona, badgeCount: number): string {
  const tier = badgeCount <= 2 ? 'few' : badgeCount <= 5 ? 'some' : 'many';
  return BADGE_INTERPRETATION_MESSAGES[tier][persona];
}
